import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { api } from './api';
import { type Membership, useAuth } from './auth';
import { useApi } from './hooks';
import { useI18n } from './i18n';


const FONTS: Record<string, string> = {
  system: '',
  inter: "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  rounded: "'Nunito', 'SF Pro Rounded', system-ui, sans-serif",
  serif: "'Source Serif 4', Georgia, 'Times New Roman', serif",
  condensed: "'Barlow Semi Condensed', 'Arial Narrow', system-ui, sans-serif",
};

const rgb = (hex: string): [number, number, number] =>
  [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
const hex = (n: number): string => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');

/** White or near-black, whichever stays readable on this colour (WCAG relative luminance). */
export function readableInk(color: string): string {
  const [r, g, b] = rgb(color).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.45 ? '#1c1917' : '#ffffff';
}

const shade = (color: string, amount: number): string => {
  const [r, g, b] = rgb(color);
  const f = (v: number): number => (amount < 0 ? v * (1 + amount) : v + (255 - v) * amount);
  return `#${hex(f(r))}${hex(f(g))}${hex(f(b))}`;
};

const mix = (color: string, other: string, weight: number): string => {
  const [r1, g1, b1] = rgb(color);
  const [r2, g2, b2] = rgb(other);
  return `#${hex(r1 * (1 - weight) + r2 * weight)}${hex(g1 * (1 - weight) + g2 * weight)}${hex(b1 * (1 - weight) + b2 * weight)}`;
};

function setLink(rel: string, href: string): void {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  if (el.href !== href) el.href = href;
}

export interface GymPolicies {
  cancelCutoffHours: number; bookingWindowDays: number; waitlist: boolean; trial: boolean; withdrawalEnabled: boolean; withdrawalDays: number;
  euWithdrawalButton: boolean; termsVersion: string; privacyVersion: string; termsUrl: string; privacyUrl: string; weekStartsOn: number; pricesIncludeTax: boolean;
}
export interface PublicGym {
  id: string; slug: string; name: string; country: string; currency: string; timezone: string; defaultLocale: string;
  branding: { primaryColor?: string; logoUrl?: string; logoDarkUrl?: string; iconUrl?: string; heroImageUrl?: string; heroText?: string; font?: string; showPlatformName?: boolean }; supportEmail: string | null; phone: string | null; website: string | null;
  paymentsEnabled: boolean; testMode?: boolean; policies: GymPolicies;
}
export interface GymInfo {
  gym: PublicGym;
  locations: { id: string; name: string; street?: string; house_number?: string; postal_code?: string; city?: string; phone?: string; email?: string; opening_hours?: Record<string, string[][]>; timezone?: string }[];
  closures: { location_id: string; start_date: string; end_date: string; closed: boolean; reason?: string }[];
  lessonTypes: { id: string; name: string; description?: string; color?: string; level?: string; duration_min?: number; capacity?: number; trial_eligible?: boolean }[];
  tokenTypes: { id: string; name: string; code?: string; color?: string; description?: string }[];
  chatEnabled: boolean;
}
interface GymValue extends GymInfo { slug: string; membership: Membership | null; reload(): void; base: string }

const GymContext = createContext<GymValue | null>(null);

export function GymProvider({ slug, children, fallback, notFound }: { slug: string; children: ReactNode; fallback: ReactNode; notFound: ReactNode }) {
  const { tenants } = useAuth();
  const i18n = useI18n();
  const info = useApi(() => api<GymInfo>(`/v1/public/${encodeURIComponent(slug)}`), [slug]);
  const membership = tenants.find((m) => m.slug === slug) ?? null;
  const gym = info.data?.gym;
  useEffect(() => {
    if (!gym) return;
    i18n.setDefaults({ tz: gym.timezone, currency: gym.currency });
    const root = document.documentElement;
    const color = /^#[0-9a-f]{6}$/i.test(gym.branding?.primaryColor ?? '') ? gym.branding.primaryColor! : '';
    if (color) {
      root.style.setProperty('--brand', color);
      // Text on the brand colour must stay readable, so it follows the colour's brightness.
      root.style.setProperty('--brand-ink', readableInk(color));
      root.style.setProperty('--brand-dark', shade(color, -0.18));
      root.style.setProperty('--brand-soft', mix(color, '#ffffff', 0.88));
    } else {
      for (const v of ['--brand', '--brand-ink', '--brand-dark', '--brand-soft']) root.style.removeProperty(v);
    }
    const font = FONTS[gym.branding?.font ?? 'system'] ?? '';
    if (font) root.style.setProperty('--font', font);
    else root.style.removeProperty('--font');
    setLink('icon', gym.branding?.iconUrl || gym.branding?.logoUrl || '/favicon.svg');
    setLink('manifest', `/v1/public/${gym.slug}/manifest.webmanifest`);
    document.title = gym.name;
    document.title = gym.name;
  }, [gym]);
  if (info.error) return <>{notFound}</>;
  if (!info.data) return <>{fallback}</>;
  return (
    <GymContext.Provider value={{ ...info.data, slug, membership, reload: info.reload, base: `/g/${slug}` }}>
      {children}
    </GymContext.Provider>
  );
}

export function useGym(): GymValue {
  const v = useContext(GymContext);
  if (!v) throw new Error('GymProvider missing');
  return v;
}

/** Base path for tenant-scoped API calls. */
export const T = (slug: string): string => `/v1/t/${encodeURIComponent(slug)}`;
